import { ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PlanFeatureService } from 'src/app/features/plans/services/plan-feature.service';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DriveAutosyncCoordinator } from './drive-autosync.coordinator';
import { DriveContextMenuCoordinator } from './drive-context-menu.coordinator';
import { DriveDateSectionLoader } from './drive-date-section.loader';
import { DriveDragDropCoordinator } from './drive-drag-drop.coordinator';
import { DriveItemPresentationPolicy } from './drive-item-presentation.policy';
import { DriveMoveDialogCoordinator } from './drive-move-dialog.coordinator';
import { DriveMoveExecutionCoordinator } from './drive-move-execution.coordinator';
import { DriveNavigationCoordinator } from './drive-navigation.coordinator';
import { DrivePreviewCoordinator } from './drive-preview.coordinator';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveSummaryCoordinator } from './drive-summary.coordinator';
import { DriveTreeCoordinator } from './drive-tree.coordinator';
import { DriveTreeMapper } from './drive-tree.mapper';
import { DriveVersionHistoryCoordinator } from './drive-version-history.coordinator';
import {
  DriveMoveState,
  DrivePreviewState,
  DriveSummaryState,
  DriveVersionState,
  DriveWorkspaceState
} from './drive-viewer.state';

export class DriveViewerComposition {
  readonly workspace = new DriveWorkspaceState();
  readonly previewState = new DrivePreviewState();
  readonly moveState = new DriveMoveState();
  readonly summaryState = new DriveSummaryState();
  readonly versionState = new DriveVersionState();
  readonly presentation = new DriveItemPresentationPolicy();
  readonly selection: DriveSelectionQuery;
  readonly navigation: DriveNavigationCoordinator;
  readonly preview: DrivePreviewCoordinator;
  readonly moveExecution: DriveMoveExecutionCoordinator;
  readonly contextMenu: DriveContextMenuCoordinator;
  readonly moveDialog: DriveMoveDialogCoordinator;
  readonly dragDrop: DriveDragDropCoordinator;
  readonly summary: DriveSummaryCoordinator;
  readonly versioning: DriveVersionHistoryCoordinator;
  readonly autosync: DriveAutosyncCoordinator;
  readonly tree: DriveTreeCoordinator;

  constructor(
    documents: DocumentService,
    language: LanguageService,
    dialog: MatDialog,
    sanitizer: DomSanitizer,
    http: HttpClient,
    logger: FrontendLoggerService,
    router: Router,
    changeDetector: ChangeDetectorRef,
    notifications: NotificationService,
    planFeatures?: PlanFeatureService
  ) {
    const translate = (key: string) => language.translate(key);
    const myDriveLabel = () => translate('drive.my_drive') || 'Mi unidad';
    this.selection = new DriveSelectionQuery(
      this.workspace, this.moveState, this.previewState, this.presentation, translate
    );

    let contextMenu!: DriveContextMenuCoordinator;
    let preview!: DrivePreviewCoordinator;
    let tree!: DriveTreeCoordinator;
    let versioning!: DriveVersionHistoryCoordinator;
    let autosync!: DriveAutosyncCoordinator;

    this.navigation = new DriveNavigationCoordinator(
      this.workspace,
      this.selection,
      {
        openFolder: (event, folderId) => contextMenu.openFolder(event, folderId),
        openFile: (event, item) => contextMenu.openFile(event, item)
      },
      (item) => preview.open(item)
    );
    preview = this.preview = new DrivePreviewCoordinator(
      this.workspace,
      this.previewState,
      this.selection,
      this.presentation,
      documents,
      http,
      sanitizer,
      logger,
      translate
    );
    this.moveExecution = new DriveMoveExecutionCoordinator(
      this.workspace,
      this.moveState,
      this.selection,
      this.presentation,
      documents,
      logger,
      translate,
      (silent) => tree.refreshPreservingState(silent)
    );
    contextMenu = this.contextMenu = new DriveContextMenuCoordinator(
      this.workspace,
      this.moveState,
      this.selection,
      this.moveExecution,
      documents,
      dialog,
      router,
      changeDetector,
      notifications,
      logger,
      translate,
      myDriveLabel,
      (silent) => tree.refreshPreservingState(silent)
    );
    this.moveDialog = new DriveMoveDialogCoordinator(
      this.moveState,
      this.selection,
      this.moveExecution,
      this.contextMenu,
      dialog,
      changeDetector,
      translate
    );
    this.dragDrop = new DriveDragDropCoordinator(
      this.workspace, this.moveState, this.selection, this.moveExecution, logger
    );
    this.summary = new DriveSummaryCoordinator(
      this.summaryState,
      this.moveState,
      this.selection,
      this.contextMenu,
      dialog,
      router,
      notifications,
      translate
    );
    versioning = this.versioning = new DriveVersionHistoryCoordinator(
      this.versionState,
      this.moveState,
      this.contextMenu,
      documents,
      dialog,
      changeDetector,
      planFeatures,
      translate
    );
    autosync = this.autosync = new DriveAutosyncCoordinator(
      this.workspace,
      (silent, finished) => tree.refreshPreservingState(silent, finished),
      () => this.previewState.previewOpen
    );
    const mapper = new DriveTreeMapper(this.presentation);
    const dateSection = new DriveDateSectionLoader(
      this.workspace,
      documents,
      sanitizer,
      changeDetector,
      this.presentation,
      translate,
      myDriveLabel
    );
    tree = this.tree = new DriveTreeCoordinator(
      this.workspace,
      documents,
      mapper,
      dateSection,
      this.navigation,
      {
        initVersioning: () => versioning.initializeFeature(),
        startAutosync: () => autosync.start(),
        stopAutosync: () => autosync.stop()
      },
      myDriveLabel
    );
  }
}
